import React, { useEffect, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js';
import { Loader2, XCircle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Onboarding Stripe embedded direttamente in Rarifex, senza redirect esterni.
 * L'artigiano compila i dati Stripe (KYC, IBAN, ecc.) restando sulla nostra
 * pagina. Al completamento chiamiamo onComplete() così il parent aggiorna lo
 * stato e mostra "Attivato".
 */
export const StripeEmbeddedOnboarding = ({ crafterId, email, onComplete, onExit }) => {
  const [connectInstance, setConnectInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) Chiedi client_secret al backend
        const r = await fetch(`${API_URL}/api/connect/account-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ crafter_id: crafterId, email }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data?.detail || 'Errore sessione onboarding');
        if (!data.publishable_key) {
          throw new Error('Publishable key Stripe non configurata (SuperAdmin → Stripe)');
        }
        // 2) Inizializza Connect.js con il client_secret
        const instance = await loadConnectAndInitialize({
          publishableKey: data.publishable_key,
          fetchClientSecret: async () => data.client_secret,
          appearance: {
            overlays: 'dialog',
            variables: {
              colorPrimary: '#008080',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              borderRadius: '8px',
              colorText: '#1e293b',
            },
          },
          locale: 'it-IT',
        });
        if (!cancelled) setConnectInstance(instance);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Errore inizializzazione');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [crafterId, email]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12" data-testid="stripe-embedded-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#008080] mb-3" />
        <p className="text-sm text-slate-600">Preparazione onboarding...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="stripe-embedded-error">
        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800">Impossibile avviare l&apos;onboarding</p>
          <p className="text-xs text-red-700 mt-1">{error}</p>
          {onExit && (
            <Button size="sm" variant="outline" className="mt-3" onClick={onExit}>
              Torna indietro
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="text-center py-8" data-testid="stripe-embedded-finished">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-3">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="font-bold text-slate-800">Ottimo, ci siamo quasi!</h3>
        <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
          Stripe sta verificando i tuoi dati. Riceverai conferma dell&apos;attivazione entro pochi minuti.
        </p>
        <Button
          className="mt-4 bg-[#008080] hover:bg-[#008080]/90 text-white"
          onClick={() => onComplete?.()}
          data-testid="stripe-embedded-continue-btn"
        >
          Continua
        </Button>
      </div>
    );
  }

  if (!connectInstance) return null;

  return (
    <div data-testid="stripe-embedded-container">
      <ConnectComponentsProvider connectInstance={connectInstance}>
        <ConnectAccountOnboarding
          onExit={() => {
            setFinished(true);
            // Callback async: aggiorno status backend/frontend
            try { onComplete?.(); } catch (_) { /* no-op */ }
          }}
        />
      </ConnectComponentsProvider>
    </div>
  );
};

export default StripeEmbeddedOnboarding;
